import { ClosableDialog } from 'components/controls/dialogs'
import { useHandleSettingsChange } from 'components/domain/stockPricing/StockPricingPresenter'
import { useUser } from 'components/providers/UserProvider'
import { createUserSubEntity, editUserAttribute } from 'lib/api'
import { getUniqueNewName } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { PriceStategyTarget } from 'shared'
import { CreateStrategyDialogContentPresenter } from './CreateStrategyDialogContentPresenter'

interface CreationStrategyDialogProps {
  onClose: (event: any, reason: 'backdropClick' | 'escapeKeyDown') => void
  open: boolean
  target: PriceStategyTarget
}

export const CreateStrategyDialog = ({
  onClose,
  open,
  target,
}: CreationStrategyDialogProps): JSX.Element => {
  const { user, activeGame } = useUser()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  if (!user) return <></>

  const handleChange = useHandleSettingsChange()
  const bulkSettings = user.bulkSettings.filter((el) => {
    return el.gameId === activeGame.idGame
  })

  return (
    <ClosableDialog maxWidth="xl" onClose={onClose} open={open}>
      <CreateStrategyDialogContentPresenter
        target={target}
        activeGame={activeGame}
        competitorNewName={getUniqueNewName(`New Competitor`, user?.competitors)}
        onCompetitorDone={async (competitor) => {
          const ok = await handleChange(() => createUserSubEntity(competitor, 'competitors'))
          if (!ok) return
          enqueueSnackbar(t('competitor.create.success'), { variant: 'success' })
        }}
        bulkSettings={bulkSettings}
        competitors={user?.competitors || []}
        newName={getUniqueNewName(`New Pricing Strategy`, user?.pricingStrategies)}
        competitionDisabled={user.subscription.featuresDisabled.includes(
          'stockPricing.competitionPricing'
        )}
        onDone={async (strategy) => {
          const ok = await handleChange(() =>
            createUserSubEntity({ ...strategy, target: target }, 'pricingStrategies')
          )
          if (!ok) return
          enqueueSnackbar(
            target === PriceStategyTarget.buyPrice
              ? t('strategy.create.successBuy')
              : t('strategy.create.successSell'),
            { variant: 'success' }
          )
          if (!user.canUsePricingBot) {
            await handleChange(() => editUserAttribute(true, 'canUsePricingBot'))
          }
          onClose({}, 'escapeKeyDown')
        }}
      />
    </ClosableDialog>
  )
}
